/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description UserRoles
 */
import { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import { Radio, Flex, Typography, Card, RadioChangeEvent } from 'antd';

import { USER_ROLES_META } from 'configs/index';
import { findUserRole, RoleBadge } from 'components/userManagement/UserBadge';
import { ANT_FORMILY_CARD_CLASS } from '@veritable/common/constants/antd';
import useAssignableRoles from 'hooks/admin/useAssignableRoles';
import { useAppStoreValues } from 'store/app';
import { filterAssignedRoles, prepareUserRoles } from 'utils/admin/userManagement';

/** Props for UserRoles */
export interface IProps {
	value: UserManagement.IUserRoleResponse[];
	onChange: (value: UserManagement.IUserRoleResponse[]) => void;
}

/**
 * UserRoles for users
 * @returns {React.FC} UserRoles
 */
const UserRoles: React.FC<IProps> = ({ value, onChange }: IProps) => {
	const { setupInfo } = useAppStoreValues();
	const USER_ROLES_DESCRIPTION_MAPPING = _.mapValues(USER_ROLES_META, ({ description }) =>
		_.get(description, [setupInfo.customerArchetype]),
	);
	const USER_ROLES_ORDER_MAPPING = _.mapValues(USER_ROLES_META, ({ displayOrder }) => displayOrder);
	const { data: assignableRoles, isLoading } = useAssignableRoles();
	const [roles, setRoles] = useState<UserManagement.IUserRoleResponse[]>([]);

	useEffect(() => {
		const filteredRoles = filterAssignedRoles(value);
		setRoles(prepareUserRoles(assignableRoles || [], filteredRoles));
	}, [value]);

	const selectedRole = useMemo(() => findUserRole(roles), [roles]);
	const selectedRoleId = _.find(roles, ({ name }) => name === selectedRole)?.id || '';

	const orderedRoles = useMemo(() => _.sortBy(assignableRoles, ({ name }) => _.get(USER_ROLES_ORDER_MAPPING, [name])), [assignableRoles]);

	/**
	 * set value & send value to props
	 */
	const handleRoleChange = (e: RadioChangeEvent) => {
		const selectedId: string = e.target.value;
		const newRoles = prepareUserRoles(assignableRoles || [], [selectedId]);
		setRoles(newRoles);
		onChange(newRoles);
	};

	/**
	 * return UserRoles
	 */
	return (
		<Card
			className={ANT_FORMILY_CARD_CLASS}
			loading={isLoading}
			title={
				<Typography.Title style={{ margin: 0 }} level={4}>
					Roles
				</Typography.Title>
			}
			style={{ marginTop: 20 }}
			styles={{ header: { borderBottom: 'none', padding: 24, paddingBottom: 4 } }}
		>
			<Radio.Group value={selectedRoleId} onChange={handleRoleChange} style={{ marginBottom: 20, width: '100%' }}>
				{_.isEmpty(roles) ? (
					<Typography.Text type='danger'>{'No roles defined'}</Typography.Text>
				) : (
					<Flex vertical gap={8} style={{ width: '100%' }}>
						{_.map(orderedRoles, ({ id, name }) => {
							const isSelected = selectedRoleId === id;
							return (
								<Card
									key={id}
									className={'user-roles-card'}
									style={{
										border: isSelected ? '1px solid #4C7BD9' : '1px solid #f0f0f0',
									}}
									onClick={() => handleRoleChange({ target: { value: id } } as RadioChangeEvent)}
								>
									<Flex align='start' gap={8}>
										<Radio key={id} value={id} style={{ margin: 0 }} />
										<Flex vertical>
											<RoleBadge role={name} mode='indicator' />
											<Typography.Text type='secondary' style={{ whiteSpace: 'normal' }}>
												{_.get(USER_ROLES_DESCRIPTION_MAPPING, [name])}
											</Typography.Text>
										</Flex>
									</Flex>
								</Card>
							);
						})}
					</Flex>
				)}
			</Radio.Group>
		</Card>
	);
};

export default UserRoles;
